import moment from "moment";
import { buildIntegrationRows } from "./tenantIntegrations.utils";
import { formatParticipantLimitUsage } from "./tenantBilling.utils";
import { formatCareSectorLabel, getTenantStatusBadge } from "./tenantsList.utils";
import {
  formatRecentActivityPrimary,
  formatRecentActivitySecondary,
} from "../Dashboard/platformDashboard.utils";

export const formatSubscriptionStatusLabel = (subscription = {}) => {
  if (subscription.on_trial) {
    return "On trial";
  }

  if (subscription.current_status) {
    return String(subscription.current_status).replace(/_/g, " ");
  }

  if (subscription.subscription_status) {
    return "Active subscription";
  }

  return "No subscription";
};

export const getSubscriptionStatusVariant = (subscription = {}, flags = {}) => {
  const status = subscription.current_status;

  if (flags.read_only || flags.participant_limit_read_only) {
    return "warning";
  }

  if (["unpaid", "past_due", "incomplete_expired"].includes(status)) {
    return "danger";
  }

  if (subscription.on_trial) {
    return "warning";
  }

  if (subscription.subscription_status || status === "active") {
    return "success";
  }

  return "neutral";
};

export const buildTenantAlerts = (tenant, { canViewBilling = false } = {}) => {
  if (!tenant) {
    return [];
  }

  const alerts = [];
  const flags = tenant.flags || {};
  const subscription = tenant.subscription || {};

  if (tenant.status === "suspended") {
    alerts.push({
      id: "suspended",
      title: "Tenant suspended",
      message: "Users cannot sign in until this tenant is activated again.",
      variant: "danger",
    });
  }

  if (flags.read_only || flags.participant_limit_read_only) {
    alerts.push({
      id: "read-only",
      title: "Read-only mode",
      message: "Write operations are blocked for this tenant.",
      variant: "warning",
    });
  }

  if (canViewBilling && subscription.on_trial && subscription.trial_end_date) {
    const trialEnd = moment(subscription.trial_end_date);
    if (trialEnd.isValid() && trialEnd.diff(moment(), "days") <= 7) {
      alerts.push({
        id: "trial-expiring",
        title: "Trial ending soon",
        message: `Trial ends ${trialEnd.format("D MMM YYYY")}.`,
        variant: "warning",
      });
    }
  }

  if (
    canViewBilling &&
    ["unpaid", "past_due", "incomplete_expired"].includes(subscription.current_status)
  ) {
    alerts.push({
      id: "billing",
      title: "Billing attention required",
      message: `Stripe status: ${formatSubscriptionStatusLabel(subscription)}.`,
      variant: "danger",
    });
  }

  const participantLimit = canViewBilling ? tenant.billing?.participant_limit : null;
  if (participantLimit?.read_only_due_to_participant_limit) {
    alerts.push({
      id: "participant-limit-read-only",
      title: "Participant limit read-only",
      message: `${formatParticipantLimitUsage(participantLimit)} — tenant is read-only until upgraded.`,
      variant: "danger",
    });
  } else if (participantLimit?.over_limit) {
    const daysLeft = participantLimit.days_until_read_only;
    alerts.push({
      id: "participant-limit",
      title: "Participant limit exceeded",
      message:
        daysLeft === null || daysLeft === undefined
          ? `${formatParticipantLimitUsage(participantLimit)} active participants.`
          : `${formatParticipantLimitUsage(participantLimit)} — read-only in ${daysLeft} day${
              daysLeft === 1 ? "" : "s"
            } unless upgraded.`,
      variant: "warning",
    });
  }

  if (canViewBilling && flags.wildcard) {
    alerts.push({
      id: "wildcard",
      title: "Wildcard billing",
      message: "Commercial wildcard concessions are enabled for this tenant.",
      variant: "neutral",
    });
  }

  return alerts;
};

export const summarizeUsageModules = (usage) => {
  const modules = usage?.modules || [];
  if (!Array.isArray(modules) || modules.length === 0) {
    return {
      averageScore: null,
      moduleCount: 0,
      activeModules: 0,
      rows: [],
    };
  }

  const scored = modules.filter((module) => module.score !== null && module.score !== undefined);
  const averageScore =
    scored.length === 0
      ? null
      : Math.round(scored.reduce((sum, module) => sum + module.score, 0) / scored.length);

  return {
    averageScore,
    moduleCount: modules.length,
    activeModules: scored.filter((module) => module.score >= 40).length,
    rows: modules.map((module) => ({
      key: module.key,
      label: module.label,
      score: module.score,
    })),
  };
};

export const summarizeIntegrations = (integrationHealth) => {
  const rows = buildIntegrationRows(integrationHealth?.integrations);
  const connected = rows.filter((row) => row.status === "connected").length;

  return {
    connected,
    total: rows.length,
    rows,
  };
};

export const formatTenantActivityRow = (activity) => ({
  id: activity.id,
  primary: formatRecentActivityPrimary(activity),
  secondary: formatRecentActivitySecondary({
    admin: activity.admin || activity.user,
    tenant: activity.tenant,
  }),
  createdAt: activity.created_at,
});

export const buildTenantQuickLinks = ({
  canViewAudit,
  canViewUsage,
  canViewIntegrations,
  canViewBilling,
  canViewTenantUsers,
}) =>
  [
    canViewBilling
      ? { id: "billing", label: "Billing details", action: "billing" }
      : null,
    canViewAudit
      ? { id: "audit", label: "View activity log", action: "activity" }
      : null,
    canViewUsage
      ? { id: "usage", label: "Module adoption", action: "usage" }
      : null,
    canViewIntegrations
      ? { id: "integrations", label: "Integrations", action: "integrations" }
      : null,
    canViewTenantUsers
      ? { id: "users", label: "Manage users", action: "users" }
      : null,
  ].filter(Boolean);

export const getTenantHeaderMetrics = (tenant, { canViewBilling = false } = {}) => {
  const counts = tenant?.counts || {};
  const regions = tenant?.regions?.length ?? 0;
  const participantLimit = canViewBilling ? tenant?.billing?.participant_limit : null;
  const limitLabel =
    participantLimit?.plan_limit !== null && participantLimit?.plan_limit !== undefined
      ? formatParticipantLimitUsage(participantLimit)
      : null;

  return [
    { id: "participants", label: "Participants", value: counts.participants ?? "—" },
    ...(limitLabel
      ? [{ id: "plan-limit", label: "Plan usage", value: limitLabel }]
      : []),
    { id: "workers", label: "Workers", value: counts.workers ?? "—" },
    { id: "managers", label: "Managers", value: counts.managers ?? "—" },
    { id: "regions", label: "Regions", value: regions || "—" },
    {
      id: "sector",
      label: "Sector",
      value: formatCareSectorLabel(tenant?.care_sector),
    },
    {
      id: "status",
      label: "Status",
      value: getTenantStatusBadge(tenant?.status)?.label || "—",
    },
  ];
};
